import {
  Extension,
  Node,
  mergeAttributes,
  wrappingInputRule,
  type Attribute,
  type Extensions,
} from '@tiptap/core'
import { Fragment, Slice, type Node as PMNode, type NodeType } from '@tiptap/pm/model'
import { Plugin, type Selection } from '@tiptap/pm/state'
import type { SpanAttributeName } from './blog-extensions.js'
import Blockquote from '@tiptap/extension-blockquote'
import Bold from '@tiptap/extension-bold'
import Document from '@tiptap/extension-document'
import Heading from '@tiptap/extension-heading'
import Italic from '@tiptap/extension-italic'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import { Gapcursor, UndoRedo } from '@tiptap/extensions'
import { formatSecondsAsTimecode } from '@kiao-voice/core/format-timecode'
import { STORED_TIMECODE_SECONDS } from '@kiao-voice/core/meeting-notes-document'

/**
 * The meeting-note schema — the ONE answer to「這份文件可以長什麼樣」.
 *
 * A meeting note is NOT stored as ProseMirror JSON. It is stored as the HTML
 * this schema serializes, and every save goes back through
 * `normalizeMeetingNotesHtml` (= render ∘ parse, both against these
 * extensions). So this schema is not the format of record; it is what DEFINES
 * the format of record. The rule that follows from that, and the reason the
 * idempotence test in `meeting-notes-extensions.test.ts` is the specification
 * of this file:
 *
 *   for every document this schema can produce, parsing it back and
 *   serializing it again must be byte-identical.
 *
 * Register a node the markdown converter cannot emit and the editor gains a
 * gesture whose result has no way of coming back from a 整理. Fail to register
 * one that a STORED note contains and the user's existing note loses that
 * structure the moment they click into it — and notes written before D5 keep
 * their old shape forever, because nothing backfills them. Both are invisible
 * until reload, which is exactly the class of bug this file exists to end: the
 * previous editor was a bare `contentEditable`, so the schema was "whatever the
 * browser happened to produce".
 *
 * Deliberately NOT registered: Link, Code, CodeBlock, Image, Strike,
 * HorizontalRule, Underline, TextStyle, Color, TextAlign, and heading levels
 * 1, 4, 5, 6. Not one of them has a branch in `meetingNotesMarkdownToDoc`, so each
 * would be a gesture the editor offers and the save throws away. (`Code` is the
 * sharpest case: the renderer uses backticks for TIMECODES, so a code span that
 * is not a timecode comes back as bare text — see the renderer's
 * `renderInline`.)
 *
 * No React import anywhere in this file. That is the same constraint
 * `blog-extensions.ts` carries, and for the same reason: plan D3 would have
 * apps/api render notes through this schema, and the API has no business
 * pulling a UI runtime. The click-to-seek behaviour therefore lives in the web
 * app's editor hook, not in a node view here.
 */

/**
 * Attribute that exists in the schema but can neither be parsed in nor rendered
 * out. Same device as `blog-extensions.ts`: dropping these from `addAttributes`
 * outright would take them away from prosemirror-tables, which reads them by
 * name.
 */
const NEUTRALIZED_ATTRIBUTE: Attribute = {
  default: null,
  parseHTML: () => null,
  renderHTML: () => ({}),
}

/**
 * `colspan` / `rowspan` have to EXIST for prosemirror-tables to work at all, so
 * they are declared rather than neutralized — but note that
 * `meetingNotesDocToMarkdown` reads a cell as a cell and has nowhere to put a
 * span, so a merged cell flattens whenever the note is copied as markdown. That is pre-existing behaviour (the old `contentEditable` did
 * the same), and the notes editor exposes no merge command, so nothing here can
 * create one; a span can only arrive by paste.
 */
function spanAttribute(name: SpanAttributeName): Attribute {
  return {
    default: 1,
    parseHTML: (element) => Number(element.getAttribute(name)) || 1,
    renderHTML: (attributes) => (attributes[name] === 1 ? {} : { [name]: String(attributes[name]) }),
  }
}

/**
 * Tiptap's stock table renders a `<colgroup>` plus an inline
 * `style="min-width: …"`, and an inline style outranks every rule in
 * `meeting-notes-document.css`. Column width is not something a meeting note
 * gets to decide, and a `<colgroup>` has no counterpart in markdown either, so
 * emitting one buys nothing and adds a second thing every reader has to skip.
 */
const NotesTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', mergeAttributes(HTMLAttributes), ['tbody', 0]]
  },
})

/**
 * A cell holds paragraphs and nothing else.
 *
 * This is the schema half of a bug the core renderer already had to fix from
 * the other side: a heading, list or quote pasted into a cell used to shatter
 * the table on save (see `meeting-notes-document.ts`, "block markup pasted into
 * a table cell"). The renderer now folds that markup back into cell text; this
 * makes the editor refuse to build it in the first place, so what is on screen
 * and what is stored agree while the user is still looking at it.
 *
 * `colwidth` (drag-resized columns) and `align` (per-cell `text-align`) are
 * neutralized: both ride in on a Word or Notion paste, and neither survives the
 * markdown round-trip.
 */
const notesCellSpec = {
  content: 'paragraph+',
  addAttributes() {
    return {
      colspan: spanAttribute('colspan'),
      rowspan: spanAttribute('rowspan'),
      colwidth: NEUTRALIZED_ATTRIBUTE,
      align: NEUTRALIZED_ATTRIBUTE,
    }
  },
}

const NotesTableCell = TableCell.extend(notesCellSpec)
const NotesTableHeader = TableHeader.extend(notesCellSpec)

/** The cell the cursor is in, if it is in one. */
function enclosingCell(selection: Selection): PMNode | null {
  const { $from } = selection
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const role = $from.node(depth).type.spec.tableRole
    if (role === 'cell' || role === 'header_cell') return $from.node(depth)
  }
  return null
}

/**
 * Every block in `fragment`, flattened to a run of paragraphs, keeping inline
 * content (text, emphasis, timecodes) and discarding only the block wrappers.
 */
function flattenToParagraphs(fragment: Fragment, paragraph: NodeType): Fragment {
  const blocks: PMNode[] = []
  let inline: PMNode[] = []

  const flushInline = () => {
    if (inline.length === 0) return
    blocks.push(paragraph.create(null, Fragment.fromArray(inline)))
    inline = []
  }

  fragment.forEach((node) => {
    if (node.isInline) {
      inline.push(node)
      return
    }
    flushInline()
    if (node.isTextblock) {
      if (node.content.size > 0) blocks.push(paragraph.create(null, node.content))
      return
    }
    // A container — list, quote, or a whole nested table. Its children are the
    // content; the wrapper is what a cell cannot hold.
    flattenToParagraphs(node.content, paragraph).forEach((child) => blocks.push(child))
  })

  flushInline()
  return Fragment.fromArray(blocks)
}

/**
 * Paste INTO a table cell, flattened to paragraphs first.
 *
 * Without this, pasting a list into a cell does not land in the cell at all:
 * `<ul>` cannot fit `paragraph+`, so ProseMirror's slice-fitting resolves the
 * mismatch the only other way it can — it SPLITS the table, leaving the list
 * loose between two half-tables. Which is the exact wreckage the core
 * renderer's `inCell` guard was written to repair from the other side, back
 * when the editor was a bare `contentEditable`.
 *
 * Flattening here is not a new policy, it is the save's existing policy applied
 * early: a cell holds `paragraph+`, so block markup inside one has to become
 * that cell's text sooner or later. Doing it at paste time only means the user sees the result while
 * they are still looking at it, instead of discovering it at the next reload.
 *
 * A single flattened paragraph keeps `openStart`/`openEnd` so it merges into
 * the text the caret is already in, rather than starting a new line.
 */
const PasteIntoCell = Extension.create({
  name: 'notesPasteIntoCell',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          transformPasted(slice, view) {
            if (!enclosingCell(view.state.selection)) return slice
            const paragraph = view.state.schema.nodes.paragraph
            const flattened = flattenToParagraphs(slice.content, paragraph)
            if (flattened.childCount === 0) return slice
            const open = flattened.childCount === 1 ? 1 : 0
            return new Slice(flattened, open, open)
          },
        },
      }),
    ]
  },
})

/**
 * `<br>`, defined here rather than imported.
 *
 * `@tiptap/extension-hard-break` is not a dependency of this repo and the plan
 * forbids adding one, but a break still has to be IN the schema: a paste from
 * Word or a browser's own Shift+Enter puts `<br>` in the DOM, and a node
 * ProseMirror does not know is dropped along with the separation it stood for
 * —「甲<br>乙」would silently become「甲乙」on screen.
 *
 * The break now SURVIVES the save: `<br>` is part of the stored shape, and
 * `meetingNotesDocToMarkdown` writes it as a CommonMark hard line break
 * (`\` + newline) in a paragraph, and as the space it visually is inside a
 * table cell, where a row is one line.
 */
const HardBreak = Node.create({
  name: 'hardBreak',
  group: 'inline',
  inline: true,
  selectable: false,
  linebreakReplacement: true,
  parseHTML: () => [{ tag: 'br' }],
  renderHTML: ({ HTMLAttributes }) => ['br', mergeAttributes(HTMLAttributes)],
  addKeyboardShortcuts() {
    const insert = () => this.editor.commands.insertContent({ type: this.name })
    return { 'Mod-Enter': insert, 'Shift-Enter': insert }
  },
})

/**
 * An ordered list that can only ever be numbered from 1 — because that is the
 * only kind the storage format can hold.
 *
 * `meetingNotesDocToMarkdown` renumbers every ordered list from 1 (there is no
 * markdown for "start at 2026"), so a `start` attribute is not a thing this
 * document type has. Two consequences, and the second one
 * is the bug:
 *
 *   - `start` is neutralized, so a paste cannot smuggle one in.
 *   - the input rule is narrowed from `^(\d+)\.\s$` to `1.` only. Stock Tiptap
 *     turns ANY「數字 + 點 + 空白」into a list starting at that number, so
 *     typing「2026. 年度計畫」produced `<ol start="2026">` on screen and
 *     `<ol><li>年度計畫</li></ol>` in the database — the year silently gone on
 *     the next reload. That is exactly the data loss `escapeLeadingBlockMarker`
 *     was written to prevent from the renderer side, reintroduced from the
 *     editor side. A year, a clause number or a price is a far more common
 *     thing to type at the start of a meeting-note line than a hand-numbered
 *     list is, and `1. ` still starts one.
 */
const NotesOrderedList = OrderedList.extend({
  addAttributes() {
    return { ...this.parent?.(), start: NEUTRALIZED_ATTRIBUTE, type: NEUTRALIZED_ATTRIBUTE }
  },
  addInputRules() {
    // Replaces the stock rule outright rather than filtering it: the point is
    // that exactly ONE prefix may start a list here.
    return [wrappingInputRule({ find: /^1[.)]\s$/, type: this.type })]
  },
})

/**
 * The timecode chip — the only thread from a note back to the audio.
 *
 * An inline ATOM, not a mark and not editable text: the visible `mm:ss` is
 * derived from `seconds`, so letting a caret inside it would let the label
 * drift away from the position it seeks to. `selectable: false` keeps it from
 * being select-highlighted as an object; clicking it seeks instead (wired up in
 * the web app's `use-notes-editor.ts`).
 *
 * `seconds` is the exact seek target, to the millisecond (`18.24`); the label
 * is the whole second it falls in. Timecodes stored before millisecond
 * precision carry whole seconds and read the same way.
 *
 * `parseHTML` is a whitelist, in the same spirit as `Figure` in
 * `blog-extensions.ts`: a `span.mn-ts` is accepted ONLY when `data-t` is what
 * the server could have written — non-negative seconds with at most three
 * decimals (`STORED_TIMECODE_SECONDS`). Anything else — a missing `data-t`,
 * `abc`, `-5`, `1e3`, or a fourth decimal — returns `false`, no rule matches,
 * and ProseMirror keeps the span's text as ordinary prose. That is the right
 * failure: the server rounds to the millisecond before it ever writes one of
 * these, so anything outside that shape can only have come from foreign HTML,
 * and coercing it here would mint a seek target nobody wrote. Degrading to
 * text loses nothing the user can see.
 */
export const Timecode = Node.create({
  name: 'timecode',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  addAttributes: () => ({
    seconds: {
      default: 0,
      parseHTML: (element) => Number(element.getAttribute('data-t')),
      renderHTML: (attributes) => ({ 'data-t': String(attributes.seconds) }),
    },
  }),
  parseHTML: () => [
    {
      tag: 'span.mn-ts',
      getAttrs: (element) => {
        const raw = element.getAttribute('data-t')
        if (raw === null || !STORED_TIMECODE_SECONDS.test(raw)) return false
        return { seconds: Number(raw) }
      },
    },
  ],
  renderHTML: ({ node, HTMLAttributes }) => [
    'span',
    // Own attributes FIRST so the serialized order is
    // `class contenteditable data-t` — byte-identical to the span the
    // hand-written renderer wrote, which is what lets an existing note and a
    // freshly rendered one be the same string rather than merely the same
    // document.
    mergeAttributes({ class: 'mn-ts', contenteditable: 'false' }, HTMLAttributes),
    // Byte-for-byte the renderer's own label, from the same shared formatter —
    // two copies of "seconds → mm:ss" is exactly how they come to disagree.
    formatSecondsAsTimecode(node.attrs.seconds as number),
  ],
})

/**
 * The meeting-note schema.
 *
 * `UndoRedo` and `Gapcursor` add nothing to the schema and are inert without a
 * live editor; they ride along so there is one array rather than two lists to
 * keep in step.
 */
export const meetingNotesExtensions: Extensions = [
  Document,
  Paragraph,
  Text,
  Heading.configure({ levels: [2, 3] }),
  Bold,
  Italic,
  BulletList,
  NotesOrderedList,
  ListItem,
  Blockquote,
  HardBreak,
  Timecode,
  NotesTable,
  TableRow,
  NotesTableHeader,
  NotesTableCell,
  PasteIntoCell,
  UndoRedo,
  Gapcursor,
]
